import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  toast,
} from '@/components/ui';
import { useTestSendMutation, type TestSendInput } from '../hooks/usePromotionalMutation';
import type { NotificationChannel } from '../types/notificationMessage';

export interface TestSendModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channel: NotificationChannel;
  templateCode?: string | null | undefined;
  rawSubject?: string | undefined;
  rawBody?: string | undefined;
  variables?: Record<string, string> | undefined;
}

export const TestSendModal = ({
  open,
  onOpenChange,
  channel,
  templateCode,
  rawSubject,
  rawBody,
  variables,
}: TestSendModalProps) => {
  const { t } = useTranslation('notifications');
  const [recipient, setRecipient] = useState('');
  const test = useTestSendMutation();

  useEffect(() => {
    if (!open) setRecipient('');
  }, [open]);

  const onSubmit = () => {
    if (!recipient.trim()) return;
    const input: TestSendInput = {
      channel,
      recipient: recipient.trim(),
      ...(templateCode ? { templateCode } : {}),
      ...(rawSubject?.trim() ? { rawSubject } : {}),
      ...(rawBody?.trim() ? { rawBody } : {}),
      ...(variables && Object.keys(variables).length > 0 ? { variables } : {}),
    };
    test.mutate(input, {
      onSuccess: () => {
        toast.success(t('promotional.test.success', '테스트 발송이 접수되었습니다.'));
        onOpenChange(false);
      },
      onError: (e) => toast.error(e.message ?? t('promotional.test.fail', '테스트 발송에 실패했습니다.')),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('promotional.test.title', '테스트 발송')}</DialogTitle>
          <DialogDescription>
            {t('promotional.test.description', '실제 발송 전에 입력한 내용으로 한 명에게 미리 발송합니다.')}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 flex flex-col gap-1.5">
          <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
            {t('promotional.test.recipient', '테스트 수신자')}
          </span>
          <Input
            type={channel === 'EMAIL' ? 'email' : 'tel'}
            size="md"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder={t('promotional.test.recipientPlaceholder', '이메일 또는 전화번호')}
            aria-label={t('promotional.test.recipient', '테스트 수신자')}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {t('common.cancel', '취소')}
          </Button>
          <Button size="sm" disabled={!recipient.trim() || test.isPending} onClick={onSubmit}>
            {test.isPending ? t('promotional.test.sending', '발송 중…') : t('promotional.test.send', '테스트 발송')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
